import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStateContext } from '../Views/Context/ContextProvider';

const InactivityTimer = () => {
    const { setToken, setUser, setType, setItems, setUserId } = useStateContext();
    const INACTIVITY_TIMEOUT = 1800; // 30 minutes in seconds
    const WARNING_TIMEOUT = 60; // Show warning after 10 seconds
    
    const [timeLeft, setTimeLeft] = useState(INACTIVITY_TIMEOUT);
    const [showTimer, setShowTimer] = useState(false);
    const navigate = useNavigate();
    const timerRef = useRef(null);
    const warningTimerRef = useRef(null);

    // Moved submitLogout inside useCallback to avoid dependency
    const handleLogout = useCallback(() => {
        const submitLogout = () => {
            setToken(null);
            setUser(null);
            setType(null);
            setItems(null);
            setUserId(null);
            
        };
        
        localStorage.removeItem('token');
        localStorage.removeItem('zone_price');
        localStorage.removeItem('address');
        localStorage.removeItem('userType');
        localStorage.removeItem('userId');
        submitLogout();
        navigate('/login');
        toast.info('You have been logged out', {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "light",
        });
    }, [navigate, setToken, setUser, setType, setItems, setUserId]);

    const resetAllTimers = useCallback(() => {
        setTimeLeft(INACTIVITY_TIMEOUT);
        setShowTimer(false);
        
        if (timerRef.current) clearInterval(timerRef.current);
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        
        warningTimerRef.current = setTimeout(() => {
            setShowTimer(true);
            
            timerRef.current = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 0) {
                        clearInterval(timerRef.current);
                        handleLogout();
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }, WARNING_TIMEOUT * 1000);
    }, [handleLogout, INACTIVITY_TIMEOUT, WARNING_TIMEOUT]);

    useEffect(() => {
        const activityEvents = [
            'mousedown', 'mousemove', 'keydown', 
            'scroll', 'touchstart', 'click',
            'input', 'change', 'wheel'
        ];

        resetAllTimers();

        activityEvents.forEach(event => {
            window.addEventListener(event, resetAllTimers);
        });

        // Handler for beforeunload event
        const handleBeforeUnload = (e) => {
            // This is for modern browsers
            e.preventDefault();
            // Chrome requires returnValue to be set
            e.returnValue = '';
        };

        // Handler for unload event (actual tab closing)
        const handleUnload = () => {
            // Perform logout when tab is closing
            handleLogout();
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('unload', handleUnload);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            
            activityEvents.forEach(event => {
                window.removeEventListener(event, resetAllTimers);
            });

            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('unload', handleUnload);
        };
    }, [resetAllTimers, handleLogout]);

    useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            @keyframes pulse {
                from { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.4); }
                to { box-shadow: 0 0 0 10px rgba(255, 107, 107, 0); }
            }
        `;
        document.head.appendChild(style);
        
        return () => document.head.removeChild(style);
    }, []);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
    };

    if (!showTimer) return null;

    return (
        <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: 'white',
            padding: '20px',
            borderRadius: '8px',
            zIndex: 1000,
            minWidth: '300px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
            textAlign: 'center',
            border: timeLeft <= 60 ? '2px solid #ff6b6b' : '2px solid #4CAF50',
            animation: timeLeft <= 60 ? 'pulse 1s infinite alternate' : 'none'
        }}>
            <h3 style={{ marginTop: 0, marginBottom: '15px' }}>
                {timeLeft <= 60 ? 'Session About to Expire!' : 'Inactivity Warning'}
            </h3>
            
            <div style={{
                fontSize: '24px',
                fontWeight: 'bold',
                margin: '15px 0',
                color: timeLeft <= 60 ? '#ff6b6b' : '#ffffff'
            }}>
                {formatTime(timeLeft)}
            </div>
            
            <p style={{ marginBottom: '20px' }}>
                {timeLeft <= 60 
                    ? 'Your session will end soon due to inactivity'
                    : 'You will be logged out automatically if inactive'}
            </p>
            
            <button 
                onClick={resetAllTimers}
                style={{
                    background: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    width: '100%',
                    transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.background = '#45a049'}
                onMouseOut={(e) => e.target.style.background = '#4CAF50'}
            >
                Continue Session
            </button>
        </div>
    );
};

export default InactivityTimer;